/* AdminSocialShare — "Sosyal Medya Paylaşımı Takip"
 * ---------------------------------------------------------------
 * Shows every active (not expired, not deleted) news item and
 * tracks which have already been shared on a given platform.
 *
 * Flow:
 *   • Admin picks a platform (FB / X / WhatsApp / Instagram / Telegram / LinkedIn).
 *   • Rows that are already shared on that platform appear muted.
 *   • Clicking "Paylaş" opens the native share dialog or the platform URL,
 *     then marks the row as shared.
 *   • Toggling an already-shared row removes the mark (useful for mistakes).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Facebook, Twitter, MessageCircle, Instagram, Send, Linkedin,
  Search, CheckCircle2, Share2, RotateCcw, ExternalLink, X,
} from "lucide-react";
import { AdminPageHeader } from "./_helpers";

/* --- Platform catalog ---------------------------------------------- */

const PLATFORMS = [
  { key: "facebook",  label: "Facebook",  icon: Facebook,       color: "#1877f2" },
  { key: "twitter",   label: "X (Twitter)", icon: Twitter,      color: "#111827" },
  { key: "whatsapp",  label: "WhatsApp",  icon: MessageCircle,  color: "#25d366" },
  { key: "instagram", label: "Instagram", icon: Instagram,      color: "#e4405f" },
  { key: "telegram",  label: "Telegram",  icon: Send,           color: "#229ed9" },
  { key: "linkedin",  label: "LinkedIn",  icon: Linkedin,       color: "#0a66c2" },
];

function platformShareUrl(platform, url, title) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title || "");
  switch (platform) {
    case "facebook":  return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case "twitter":   return `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    case "whatsapp":  return `https://wa.me/?text=${t}%20${u}`;
    case "telegram":  return `https://t.me/share/url?url=${u}&text=${t}`;
    case "linkedin":  return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case "instagram": return null; // Instagram has no direct web share URL
    default:          return url;
  }
}

function openShare(platform, newsSlug, newsTitle) {
  const base =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    "";
  // Prefer the server-rendered OG URL so link previews work on bots.
  const backend =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const ogUrl = `${backend}/api/og/news/${newsSlug}?v=${Date.now()}`;
  const spaUrl = `${base}/haber/${newsSlug}`;
  const url = platform === "instagram" ? spaUrl : ogUrl;

  if (platform === "instagram") {
    // Best-effort: copy caption text and jump to Instagram site.
    try {
      navigator.clipboard?.writeText(`${newsTitle || ""}\n${spaUrl}`);
      toast.success("Metin kopyalandı · Instagram'da yapıştırabilirsin");
    } catch { /* noop */ }
    window.open("https://www.instagram.com/", "_blank", "noopener");
    return true;
  }

  const share = platformShareUrl(platform, url, newsTitle);
  if (!share) {
    toast.error("Bu platform için paylaşım URL'si yok");
    return false;
  }
  window.open(share, "_blank", "noopener,width=680,height=640");
  return true;
}

const trDate = (s) => {
  try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s || "—"; }
};

/* --- Component ----------------------------------------------------- */

export default function AdminSocialShare() {
  const [platform, setPlatform] = useState("facebook");
  const [search, setSearch] = useState("");
  const [provinceId, setProvinceId] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provinces, setProvinces] = useState([]);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        platform,
        limit: "500",
        only_pending: String(onlyPending),
      });
      if (search.trim()) params.set("search", search.trim());
      if (provinceId) params.set("province_id", provinceId);
      const r = await api.get(`/news/share-tracker?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error("Veriler yüklenemedi: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [platform, search, provinceId, onlyPending]);

  useEffect(() => {
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data || []));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounce search text
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const current = useMemo(() => PLATFORMS.find((p) => p.key === platform), [platform]);

  const toggleShared = async (newsId, nextShared) => {
    setBusyIds((prev) => new Set(prev).add(newsId));
    try {
      await api.post(`/news/${newsId}/mark-shared`, {
        platform,
        state: nextShared ? "shared" : "unshared",
      });
      // Optimistic local update
      setData((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((it) => {
          if (it.id !== newsId) return it;
          return {
            ...it,
            shared: nextShared,
            shared_at: nextShared ? new Date().toISOString() : null,
          };
        });
        const shared_count = items.filter((i) => i.shared).length;
        return {
          ...prev,
          items,
          shared_count,
          pending_count: items.length - shared_count,
        };
      });
    } catch (e) {
      toast.error("İşlem başarısız: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(newsId); return n; });
    }
  };

  const handleShareClick = async (row) => {
    const ok = openShare(platform, row.slug, row.title);
    if (ok && !row.shared) await toggleShared(row.id, true);
  };

  const items = data?.items || [];

  return (
    <div data-testid="admin-social-share">
      <AdminPageHeader
        title="Sosyal Medya Paylaşımları"
        subtitle={
          data
            ? `${data.total} aktif haber · ${data.shared_count} paylaşıldı · ${data.pending_count} bekliyor`
            : "Haberlerin platform bazlı paylaşım durumunu takip edin"
        }
      />

      {/* Platform tabs */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-3 flex flex-wrap gap-2">
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const active = p.key === platform;
          return (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              data-testid={`share-platform-${p.key}`}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${
                active
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
              style={active ? { backgroundColor: p.color } : {}}
            >
              <Icon className="w-4 h-4" />
              <span>{p.label}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-3 flex flex-wrap gap-2 items-center sticky top-0 z-10">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Başlık / spot ara..."
            className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-sm"
            data-testid="share-search"
          />
        </div>
        <select
          value={provinceId}
          onChange={(e) => setProvinceId(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
          data-testid="share-province-filter"
        >
          <option value="">Tüm İller</option>
          {provinces.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <label
          className={`inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 border rounded-md transition-colors ${
            onlyPending
              ? "border-[#e11d2a] bg-red-50 text-[#e11d2a] font-semibold"
              : "border-gray-200 bg-white hover:bg-gray-50"
          }`}
          data-testid="share-only-pending-toggle"
        >
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
            className="w-4 h-4 accent-[#e11d2a]"
          />
          <span>Sadece paylaşılmayanlar</span>
        </label>
      </div>

      {/* Platform header */}
      <div
        className="rounded-lg p-4 mb-3 text-white shadow-sm flex items-center justify-between gap-3"
        style={{ backgroundColor: current?.color || "#111827" }}
        data-testid="share-current-platform-header"
      >
        <div className="flex items-center gap-3 min-w-0">
          {current ? <current.icon className="w-6 h-6 flex-shrink-0" /> : null}
          <div className="min-w-0">
            <div className="font-bold text-lg truncate">{current?.label} Paylaşımları</div>
            <div className="text-xs opacity-90">
              Aşağıdaki haberlere tıklayarak {current?.label} üzerinde paylaş. Paylaştıkların otomatik işaretlenir.
            </div>
          </div>
        </div>
        {data && (
          <div className="text-right whitespace-nowrap">
            <div className="text-2xl font-bold leading-none">{data.shared_count}</div>
            <div className="text-[11px] opacity-90 uppercase tracking-wider">paylaşıldı</div>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-gray-100 rounded-lg p-10 text-center text-sm text-gray-500" data-testid="share-loading">
          Yükleniyor...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-lg p-10 text-center text-sm text-gray-500" data-testid="share-empty">
          Kriterlere uygun aktif haber bulunamadı.
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-lg overflow-hidden" data-testid="share-list">
          <ul className="divide-y divide-gray-100">
            {items.map((row) => {
              const busy = busyIds.has(row.id);
              const shared = row.shared;
              return (
                <li
                  key={row.id}
                  className={`flex items-start sm:items-center gap-3 p-3 sm:p-4 flex-col sm:flex-row ${
                    shared ? "opacity-55 bg-gray-50" : "hover:bg-gray-50"
                  }`}
                  data-testid={`share-row-${row.id}`}
                >
                  {/* Thumb */}
                  <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                    {row.cover_image ? (
                      <img src={row.cover_image} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">yok</div>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="min-w-0 flex-1 w-full">
                    <div className="flex items-center gap-2 flex-wrap">
                      {shared && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800">
                          <CheckCircle2 className="w-3 h-3" />
                          Paylaşıldı
                        </span>
                      )}
                      <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {row.province_name}
                      </span>
                      <span className="text-[11px] text-gray-400">{trDate(row.publish_at)}</span>
                    </div>
                    <div className={`font-semibold mt-1 ${shared ? "line-through text-gray-500" : "text-gray-900"}`}>
                      <a
                        href={`/haber/${row.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline inline-flex items-start gap-1"
                        title="Haberi aç"
                      >
                        <span>{row.title}</span>
                        <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0 opacity-60" />
                      </a>
                    </div>
                    {row.spot && (
                      <div className="text-xs text-gray-500 line-clamp-1 mt-0.5">{row.spot}</div>
                    )}
                    {shared && row.shared_at && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        Son işaret: {new Date(row.shared_at).toLocaleString("tr-TR")}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                    {shared ? (
                      <button
                        onClick={() => toggleShared(row.id, false)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`share-unmark-${row.id}`}
                        title="İşareti kaldır"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>İşareti Kaldır</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleShareClick(row)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 shadow-sm"
                        style={{ backgroundColor: current?.color || "#111827" }}
                        data-testid={`share-do-${row.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                        <span>Paylaş & İşaretle</span>
                      </button>
                    )}
                    <button
                      onClick={() => toggleShared(row.id, !shared)}
                      disabled={busy}
                      className={`inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 ${shared ? "" : "text-gray-500"}`}
                      title={shared ? "İşareti kaldır" : "Sadece işaretle (paylaşma)"}
                      data-testid={`share-toggle-${row.id}`}
                    >
                      {shared ? <X className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
